import React, { useState, useEffect } from 'react';
import ColorPicker from './ColorPicker';
import { DEFAULT_COLOR_PALETTES } from '../config/colorPalettes';
import styles from '../styles/components/CustomPaletteEditor.module.scss';

const CustomPaletteEditor = ({
    isOpen,
    onClose,
    onSave,
    initialPalette = null,
    basePalette = 'default'
}) => {
    const [paletteName, setPaletteName] = useState('');
    const [colors, setColors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState({});

    // カラーの説明
    const colorDescriptions = {
        primary: 'メインカラー - ボタンやリンクなどの主要な要素',
        primaryDark: 'プライマリ暗色 - ホバー時やアクティブ状態',
        secondary: 'セカンダリカラー - 補助的な要素',
        accent: 'アクセントカラー - 強調したい要素',
        background: '背景色 - ページの基本背景',
        surface: 'サーフェス色 - カードやパネルの背景',
        text: 'テキスト色 - 基本的な文字色',
        textMuted: 'ミュートテキスト色 - 補助的な文字色',
        border: 'ボーダー色 - 境界線や枠線'
    };

    // 初期化
    useEffect(() => {
        if (isOpen) {
            if (initialPalette) {
                // 既存のカスタムパレットを編集
                setPaletteName(initialPalette.name || '');
                setColors(initialPalette.colors || {});
            } else {
                // 新規作成（ベースパレットから開始）
                const baseColors = DEFAULT_COLOR_PALETTES[basePalette]?.colors || DEFAULT_COLOR_PALETTES.default.colors;
                const basePaletteName = DEFAULT_COLOR_PALETTES[basePalette]?.name || 'デフォルト';
                setPaletteName(basePalette ? `${basePaletteName}のコピー` : '');
                setColors({ ...baseColors });
            }
            setErrors({});
        }
    }, [isOpen, initialPalette, basePalette]);

    const handleColorChange = (colorKey, newColor) => {
        setColors(prev => ({
            ...prev,
            [colorKey]: newColor
        }));

        // エラーをクリア
        if (errors[colorKey]) {
            setErrors(prev => ({
                ...prev,
                [colorKey]: null
            }));
        }
    };

    const validateColor = (color) => {
        const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
        return hexPattern.test(color);
    };

    const validateForm = () => {
        const newErrors = {};

        // パレット名の検証
        if (!paletteName.trim()) {
            newErrors.name = 'パレット名を入力してください';
        } else if (paletteName.trim().length > 50) {
            newErrors.name = 'パレット名は50文字以内で入力してください';
        }

        // 色の検証
        Object.entries(colors).forEach(([key, color]) => {
            if (!validateColor(color)) {
                newErrors[key] = '有効な16進数カラーコードを入力してください（例: #FF0000）';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);

        try {
            const customPalette = {
                name: paletteName.trim(),
                colors: { ...colors },
                isCustom: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            await onSave(customPalette);
            onClose();
        } catch (error) {
            console.error('カスタムパレットの保存に失敗:', error);
            setErrors({ general: 'パレットの保存に失敗しました。もう一度お試しください。' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        const baseColors = DEFAULT_COLOR_PALETTES[basePalette]?.colors || DEFAULT_COLOR_PALETTES.default.colors;
        setColors({ ...baseColors });
        setErrors({});
    };

    const generateRandomPalette = () => {
        const generateRandomColor = () => {
            return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
        };

        const randomColors = {};
        Object.keys(colors).forEach(key => {
            randomColors[key] = generateRandomColor();
        });

        setColors(randomColors);
        setErrors({});
    };

    if (!isOpen) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2>
                        {initialPalette
                            ? 'カラーパレットを編集'
                            : basePalette
                                ? `「${DEFAULT_COLOR_PALETTES[basePalette]?.name || basePalette}」をベースにカスタムパレットを作成`
                                : 'カスタムカラーパレットを作成'
                        }
                    </h2>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        ×
                    </button>
                </div>

                <div className={styles.content}>
                    {errors.general && (
                        <div className={styles.errorMessage}>
                            {errors.general}
                        </div>
                    )}

                    <div className={styles.nameSection}>
                        <label htmlFor="paletteName" className={styles.label}>
                            パレット名 <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="paletteName"
                            type="text"
                            value={paletteName}
                            onChange={(e) => setPaletteName(e.target.value)}
                            placeholder="例: マイカスタムパレット"
                            className={`${styles.nameInput} ${errors.name ? styles.error : ''}`}
                            disabled={isSaving}
                        />
                        {errors.name && (
                            <div className={styles.fieldError}>{errors.name}</div>
                        )}
                    </div>

                    <div className={styles.actionsSection}>
                        <button
                            className={styles.actionButton}
                            onClick={handleReset}
                            disabled={isSaving}
                        >
                            リセット
                        </button>
                        <button
                            className={styles.actionButton}
                            onClick={generateRandomPalette}
                            disabled={isSaving}
                        >
                            ランダム生成
                        </button>
                    </div>

                    <div className={styles.colorsSection}>
                        <h3>カラー設定</h3>
                        <div className={styles.colorGrid}>
                            {Object.entries(colors).map(([colorKey, colorValue]) => (
                                <div key={colorKey} className={styles.colorItem}>
                                    <ColorPicker
                                        color={colorValue}
                                        onChange={(newColor) => handleColorChange(colorKey, newColor)}
                                        label={`${colorKey} (${colorDescriptions[colorKey] || ''})`}
                                        disabled={isSaving}
                                    />
                                    {errors[colorKey] && (
                                        <div className={styles.fieldError}>{errors[colorKey]}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.previewSection}>
                        <h3>プレビュー</h3>
                        <div
                            className={styles.previewCard}
                            style={{
                                backgroundColor: colors.background,
                                color: colors.text,
                                border: `1px solid ${colors.border}`
                            }}
                        >
                            <h4 style={{ color: colors.primary }}>サンプルタイトル</h4>
                            <p style={{ color: colors.textMuted }}>
                                これはプレビューテキストです。作成したカラーパレットがどのように表示されるかを確認できます。
                            </p>
                            <div className={styles.previewButtons}>
                                <button
                                    style={{
                                        backgroundColor: colors.primary,
                                        color: colors.background,
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        marginRight: '8px'
                                    }}
                                >
                                    プライマリボタン
                                </button>
                                <button
                                    style={{
                                        backgroundColor: colors.accent,
                                        color: colors.background,
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px'
                                    }}
                                >
                                    アクセントボタン
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.footer}>
                    <button
                        className={styles.cancelButton}
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        キャンセル
                    </button>
                    <button
                        className={styles.saveButton}
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? '保存中...' : (initialPalette ? '更新' : '作成')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomPaletteEditor;
